// SPDX-License-Identifier: AGPL-3.0-or-later

import {useFormSubmit} from '@app/features/app/hooks/useFormSubmit';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import Guilds from '@app/features/guild/state/Guilds';
import {GuildTagBadge} from '@app/features/guild_tag/components/GuildTagBadge';
import badgeStyles from '@app/features/guild_tag/components/GuildTagBadge.module.css';
import {GuildTagChip} from '@app/features/guild_tag/components/GuildTagLabel';
import styles from '@app/features/guild_tag/components/GuildTagTab.module.css';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Input} from '@app/features/ui/components/form/FormInput';
import Users from '@app/features/user/state/Users';
import {useRemoteFormReset} from '@app/lib/forms/RemoteFormReset';
import {
	encodeGuildTagBadge,
	GUILD_TAG_BADGE_COLORS,
	GUILD_TAG_BADGE_ICONS,
	GUILD_TAG_MAX_LENGTH,
	GUILD_TAG_MIN_LENGTH,
	GUILD_TAG_PATTERN,
} from '@fluxer/constants/src/GuildTagConstants';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {ProhibitIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';

const TAG_FORMAT_DESCRIPTOR = msg({
	message: 'Use 2 to 4 capital letters or digits.',
	comment: 'Validation message on the server tag field in community settings.',
});
const TAG_SAVED_DESCRIPTOR = msg({
	message: 'Server tag saved.',
	comment: 'Success toast after saving the server tag in community settings.',
});
const TAG_REMOVED_DESCRIPTOR = msg({
	message: 'Server tag removed.',
	comment: 'Success toast after removing the server tag in community settings.',
});
const BADGE_ICON_DESCRIPTOR = msg({
	message: 'Badge {number}',
	comment: 'Accessible label of one icon in the server tag badge picker. number is its position in the list.',
});
const BADGE_COLOR_DESCRIPTOR = msg({
	message: 'Color {number}',
	comment: 'Accessible label of one color in the server tag badge picker. number is its position in the list.',
});
const NO_BADGE_DESCRIPTOR = msg({
	message: 'No badge',
	comment: 'Accessible label of the empty choice in the server tag badge picker.',
});

interface FormInputs {
	guild_tag: string;
}

// Turkish keyboards type i as İ here, and anything outside the tag alphabet is dropped as it is typed.
function normalizeTag(value: string): string {
	return value
		.toLocaleUpperCase('tr-TR')
		.replace(/[^0-9A-ZÇĞİÖŞÜ]/g, '')
		.slice(0, GUILD_TAG_MAX_LENGTH);
}

function splitBadge(badge: number | null): {icon: number | null; color: number} {
	if (badge === null) return {icon: null, color: 0};
	return {icon: Math.floor(badge / GUILD_TAG_BADGE_COLORS.length), color: badge % GUILD_TAG_BADGE_COLORS.length};
}

const GuildTagTab: React.FC<{guildId: string}> = observer(({guildId}) => {
	const {i18n} = useLingui();
	const guild = Guilds.getGuild(guildId);
	const savedTag = guild?.guildTag ?? '';
	const savedBadge = guild?.guildTagBadge ?? null;
	const [icon, setIcon] = useState(() => splitBadge(savedBadge).icon);
	const [color, setColor] = useState(() => splitBadge(savedBadge).color);
	const [isRemoving, setIsRemoving] = useState(false);
	const form = useForm<FormInputs>({defaultValues: {guild_tag: savedTag}});
	const remoteValues = useMemo<FormInputs>(() => ({guild_tag: savedTag}), [savedTag]);
	useRemoteFormReset<FormInputs>({form, identityKey: guildId, remoteValues});
	useEffect(() => {
		const saved = splitBadge(savedBadge);
		setIcon(saved.icon);
		setColor(saved.color);
	}, [savedBadge]);
	const tag = form.watch('guild_tag');
	const badge = icon === null ? null : encodeGuildTagBadge(icon, color);
	const onSubmit = useCallback(
		async (data: FormInputs) => {
			await GuildCommands.update(guildId, {guild_tag: data.guild_tag, guild_tag_badge: badge});
			ToastCommands.createToast({type: 'success', children: i18n._(TAG_SAVED_DESCRIPTOR)});
		},
		[guildId, badge, i18n],
	);
	const {handleSubmit, isSubmitting} = useFormSubmit({form, onSubmit, defaultErrorField: 'guild_tag'});
	const handleRemove = useCallback(async () => {
		setIsRemoving(true);
		try {
			await GuildCommands.update(guildId, {guild_tag: null});
			ToastCommands.createToast({type: 'success', children: i18n._(TAG_REMOVED_DESCRIPTOR)});
		} catch (error) {
			form.setError('guild_tag', {message: (error as {body?: {message?: string}}).body?.message});
		} finally {
			setIsRemoving(false);
		}
	}, [guildId, form, i18n]);
	if (!guild) return null;
	return (
		<div className={styles.container} data-flx="guild-tag.guild-tag-tab.container">
			<p className={styles.intro} data-flx="guild-tag.guild-tag-tab.intro">
				<Trans>
					Members can show this tag beside their name, also in other communities. Wearing it is each member's own choice
					in their profile settings.
				</Trans>
			</p>
			<div className={styles.preview} data-flx="guild-tag.guild-tag-tab.preview">
				<span className={styles.previewName} data-flx="guild-tag.guild-tag-tab.preview-name">
					{Users.currentUser?.displayName}
				</span>
				<GuildTagChip
					tag={tag || '····'}
					badge={badge}
					size="md"
					className={clsx(!tag && styles.previewEmpty)}
					data-flx="guild-tag.guild-tag-tab.preview-chip"
				/>
			</div>
			<Form form={form} onSubmit={handleSubmit} data-flx="guild-tag.guild-tag-tab.form.submit">
				<div className={styles.formBody} data-flx="guild-tag.guild-tag-tab.form-body">
					<Input
						data-flx="guild-tag.guild-tag-tab.input"
						{...form.register('guild_tag', {
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
								form.setValue('guild_tag', normalizeTag(event.target.value));
							},
							pattern: {value: GUILD_TAG_PATTERN, message: i18n._(TAG_FORMAT_DESCRIPTOR)},
						})}
						label={i18n._(msg({message: 'Tag', comment: 'Field label for the server tag text in community settings.'}))}
						footer={
							<span className={styles.help} data-flx="guild-tag.guild-tag-tab.input-footer">
								<Trans>2 to 4 letters or digits. You can change it once an hour.</Trans>
							</span>
						}
						minLength={GUILD_TAG_MIN_LENGTH}
						maxLength={GUILD_TAG_MAX_LENGTH}
						autoComplete="off"
						error={form.formState.errors.guild_tag?.message}
					/>
					<fieldset className={styles.picker} data-flx="guild-tag.guild-tag-tab.icon-picker">
						<legend className={styles.pickerLabel} data-flx="guild-tag.guild-tag-tab.icon-picker-label">
							<Trans>Badge</Trans>
						</legend>
						<div className={styles.iconGrid} data-flx="guild-tag.guild-tag-tab.icon-grid">
							<button
								type="button"
								className={clsx(styles.iconButton, icon === null && styles.selected)}
								aria-pressed={icon === null}
								aria-label={i18n._(NO_BADGE_DESCRIPTOR)}
								onClick={() => setIcon(null)}
								data-flx="guild-tag.guild-tag-tab.no-badge"
							>
								<ProhibitIcon size={20} data-flx="guild-tag.guild-tag-tab.no-badge-icon" />
							</button>
							{GUILD_TAG_BADGE_ICONS.map((name, index) => (
								<button
									key={name}
									type="button"
									className={clsx(styles.iconButton, icon === index && styles.selected)}
									aria-pressed={icon === index}
									aria-label={i18n._(BADGE_ICON_DESCRIPTOR, {number: index + 1})}
									onClick={() => setIcon(index)}
									data-flx="guild-tag.guild-tag-tab.icon-button"
								>
									<GuildTagBadge
										badge={encodeGuildTagBadge(index, color)}
										className={styles.iconGlyph}
										data-flx="guild-tag.guild-tag-tab.icon-glyph"
									/>
								</button>
							))}
						</div>
					</fieldset>
					<fieldset className={styles.picker} data-flx="guild-tag.guild-tag-tab.color-picker">
						<legend className={styles.pickerLabel} data-flx="guild-tag.guild-tag-tab.color-picker-label">
							<Trans>Badge color</Trans>
						</legend>
						<div className={styles.colorRow} data-flx="guild-tag.guild-tag-tab.color-row">
							{GUILD_TAG_BADGE_COLORS.map((value, index) => (
								<button
									key={value}
									type="button"
									className={clsx(styles.colorButton, badgeStyles.tones, color === index && styles.selected)}
									data-color={index}
									aria-pressed={color === index}
									aria-label={i18n._(BADGE_COLOR_DESCRIPTOR, {number: index + 1})}
									disabled={icon === null}
									onClick={() => setColor(index)}
									data-flx="guild-tag.guild-tag-tab.color-button"
								/>
							))}
						</div>
					</fieldset>
					<div className={styles.actions} data-flx="guild-tag.guild-tag-tab.actions">
						<Button
							type="button"
							variant="secondary"
							submitting={isRemoving}
							disabled={!savedTag || isSubmitting}
							onClick={handleRemove}
							data-flx="guild-tag.guild-tag-tab.button.remove"
						>
							<Trans>Remove</Trans>
						</Button>
						<Button
							type="submit"
							submitting={isSubmitting}
							disabled={isRemoving || !tag}
							data-flx="guild-tag.guild-tag-tab.button.submit"
						>
							<Trans>Save</Trans>
						</Button>
					</div>
				</div>
			</Form>
		</div>
	);
});

export default GuildTagTab;
